import { useCallback } from "react";
import { useApiDispatch } from "@app/contexts/ApiDispatch";
import { useMountContext } from "@app/components/Mount";
import { ApiDispatcherInterface } from "@app/backendApi/types";
import { errorModal } from "@app/components/ErrorModal";

/**
 * Dispatches an API action and puts a modal up if it fails.
 *
 * Every call site used to end in a bare `.then()` chain, so a rejected request did
 * nothing at all — deleting an item removed it on the server and left it in the sidebar
 * with not a word in the console. A failure that changes nothing on screen is
 * indistinguishable from one that never happened.
 *
 * The rejection is re-thrown after reporting, so a caller's own chain still stops. What
 * it must not do is carry on: `.then(refetch)` after a failed write would paper over it.
 * Call sites end in `.catch(() => {})` because this has already told the user.
 *
 * Wrapped here rather than inside ApiDispatcher because showing a modal needs the mount
 * stack, and that lives inside the dispatcher's own provider.
 */
export function useApiCall() {
  const apiDispatch = useApiDispatch();
  const mount = useMountContext();

  return useCallback(
    <R,>(
      action: (dispatch: ApiDispatcherInterface["dispatch"]) => Promise<R>,
      /** What was being attempted, for the modal: "delete this item". */
      attempting: string
    ): Promise<R> =>
      apiDispatch(action).catch((error: unknown) => {
        mount.push({ element: errorModal(attempting, error) });
        throw error;
      }),
    [apiDispatch, mount]
  );
}
